import { css, styled } from 'styled-components'

import { QueryLoadingManager } from '@b-stock/fe-core/bstock-next/components'
import { getBalancePortalLinkQuery } from '@b-stock/fe-core/bstock-next/queries'
import { Banner, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors } from '@b-stock/fe-core/design-system'

import { ActionLink } from '@/buyer-portal/components/ActionLink'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import type { NetTermsPayMethod } from '../../shared'

const StyledBanner = styled(Banner)<{ $neutral?: boolean }>`
  text-align: left;
  ${({ $neutral }) =>
    $neutral &&
    css`
      && {
        background-color: ${Colors.Semantic.Neutral.white};
        border-color: ${Colors.Semantic.Neutral.medium_grey};
      }
    `}
`

export const BalancePortalLink = ({
  chunks,
  balanceMethod,
  onLinkClick,
}: {
  chunks: React.ReactNode
  balanceMethod?: NetTermsPayMethod
  onLinkClick?: () => void
}) => {
  const { trackButtonClicked } = useAnalytics()
  const portalLinkResult = getBalancePortalLinkQuery.useQuery(
    {
      balanceBuyerId: balanceMethod?.processorCustomerId ?? '',
    },
    {
      enabled: !!balanceMethod,
      placeholderData: {
        url: '',
      },
      staleTime: 1000 * 60 * 60 * 10, // Link expires after 10 hours
    }
  )
  const hasNoAvailableCredit =
    (balanceMethod?.details.creditInfo?.availableCreditInCents ?? 0) <= 0
  return (
    <QueryLoadingManager
      {...portalLinkResult}
      EmptyComponent={() => (
        <FormattedMessage id="CheckoutPage.payWithTerms.balancePortal" />
      )}
      ErrorComponent={() => (
        <FormattedMessage id="CheckoutPage.payWithTerms.balancePortal" />
      )}
    >
      {({ data }) => (
        <ActionLink
          href={data.url}
          disabled={portalLinkResult.isPlaceholderData}
          onClick={(e) => {
            e.stopPropagation()
            if (hasNoAvailableCredit) {
              trackButtonClicked({
                buttonName: 'balance_portal_terms',
              })
            }
            onLinkClick?.()
          }}
        >
          {chunks}
        </ActionLink>
      )}
    </QueryLoadingManager>
  )
}

export const InformativeBanner = ({
  statusText,
  balanceMethod,
  onLinkClick,
}: {
  statusText: string
  balanceMethod?: NetTermsPayMethod
  onLinkClick?: () => void
}) => (
  <StyledBanner
    $neutral={
      statusText === 'qualificationDenied' || statusText === 'accountSuspended'
    }
    type={
      statusText === 'qualificationDenied' || statusText === 'accountSuspended'
        ? 'info'
        : 'warning'
    }
    hideIcon
  >
    <FormattedMessage
      id={`CheckoutPage.payWithTerms.banner.${statusText}`}
      values={{
        link: (chunks: React.ReactNode) =>
          statusText === 'bstockDisabled' ? (
            <ActionLink
              href="https://bstock.com/buyer-contact-us/"
              onClick={(e) => e.stopPropagation()}
            >
              {chunks}
            </ActionLink>
          ) : (
            <BalancePortalLink
              chunks={chunks}
              balanceMethod={balanceMethod}
              onLinkClick={onLinkClick}
            />
          ),
        b: (text) => <b>{text}</b>,
      }}
    />
  </StyledBanner>
)
